import React, { useEffect, useState } from 'react';
import { Alert, Box, Button, CircularProgress } from '@mui/material';
import { CreateNewFolderOutlined as NewProjectIcon } from '@mui/icons-material';
import SidePanel, { PanelSection } from '../SidePanel';
import ProjectSectionFields, { PROJECT_SECTIONS } from './ProjectFields';
import RegexHelp from './RegexHelp';
import { createProject, EMPTY_PROJECT_FORM, validateProjectForm } from '../../utils/projectsService';
import { GREEN, GREEN_DARK, GREEN_SOFT } from '../home/homeTheme';

/** Icona del progetto: quadrato verde chiaro con la cartella. */
export function ProjectBadge({ size = 32, icon }) {
  const Icon = icon || NewProjectIcon;
  return (
    <Box sx={{ width: size, height: size, borderRadius: size > 40 ? 3 : 2, bgcolor: GREEN_SOFT, color: GREEN_DARK, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
      <Icon sx={{ fontSize: size * 0.56 }} />
    </Box>
  );
}

/** Pannello laterale di creazione: tutte le sezioni del progetto; chi crea diventa Service Manager del progetto. */
export default function NewProjectPanel({ open, onClose, onCreated, customers, serviceTypes, userId }) {
  const [form, setForm] = useState(EMPTY_PROJECT_FORM);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [regexHelp, setRegexHelp] = useState(false);

  useEffect(() => {
    if (!open) return;
    setForm(EMPTY_PROJECT_FORM);
    setErrors({});
  }, [open]);

  const change = (field, value) => {
    setForm((f) => ({ ...f, [field]: value, ...(field === 'customer_id' ? { yubiq_code: '' } : {}) }));
    setErrors((e) => ({ ...e, [field]: undefined, general: undefined }));
  };

  const save = async () => {
    const e = validateProjectForm(form);
    setErrors(e);
    if (Object.keys(e).length) return;
    setSaving(true);
    try {
      const id = await createProject(form, userId);
      onCreated(id);
    } catch (err) {
      setErrors({ general: `Not created: ${err.message}` });
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <SidePanel
        open={open}
        onClose={onClose}
        busy={saving}
        icon={<ProjectBadge size={46} />}
        title="New project"
        subtitle="You’ll be added as its Service Manager"
        footer={
          <>
            <Button color="inherit" onClick={onClose} disabled={saving} sx={{ textTransform: 'none' }}>
              Cancel
            </Button>
            <Button variant="contained" color="success" onClick={save} disabled={saving} sx={{ textTransform: 'none', fontWeight: 700, boxShadow: 'none', minWidth: 130, bgcolor: GREEN }}>
              {saving ? <CircularProgress size={18} color="inherit" /> : 'Create project'}
            </Button>
          </>
        }
      >
        {Object.entries(PROJECT_SECTIONS).map(([key, section]) => (
          <PanelSection key={key} title={section.title}>
            <Box sx={{ pt: 0.75 }}>
              <ProjectSectionFields
                section={key}
                form={form}
                errors={errors}
                onChange={change}
                customers={customers}
                serviceTypes={serviceTypes}
                onRegexHelp={() => setRegexHelp(true)}
                autoFocus={key === 'project'}
              />
            </Box>
          </PanelSection>
        ))}
        {errors.general && <Alert severity="error">{errors.general}</Alert>}
      </SidePanel>
      <RegexHelp
        open={regexHelp}
        onClose={() => setRegexHelp(false)}
        regex={form.regex}
        onUse={(regex) => {
          change('regex', regex);
          setRegexHelp(false);
        }}
      />
    </>
  );
}
